import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle2, Info, Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent as ConfirmContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { formatCurrency, formatDate, formatDateTime, formatNumber } from '@/lib/format';
import {
  useArchiveBacklog,
  useReconcile,
  yearStart,
} from './payment-review.api';
import { outcomeLabelKey } from './types';

/**
 * Find old payments — the backward-looking half of this page.
 *
 * Two steps that are deliberately not one button. Deciding not to chase the old
 * backlog is a different kind of decision from searching for payments, and
 * should not ride along with it.
 *
 * Both steps preview first: `apply: false` stages exactly what the API would do
 * and throws the changes away, so nobody authorises a mass write to historical
 * payments sight unseen.
 */

function Note({
  tone,
  children,
}: {
  tone: 'info' | 'warning' | 'success' | 'error';
  children: React.ReactNode;
}) {
  const Icon =
    tone === 'success' ? CheckCircle2 : tone === 'info' ? Info : AlertCircle;
  const classes = {
    info: 'border-border bg-muted text-foreground',
    warning: 'border-warning/30 bg-warning/10 text-foreground',
    success: 'border-success/30 bg-success/10 text-foreground',
    error: 'border-destructive/30 bg-destructive/8 text-destructive',
  }[tone];

  return (
    <div className={`flex items-start gap-2 rounded-xl border p-3 text-sm ${classes}`}>
      <Icon className="mt-0.5 size-4 shrink-0" />
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** A labelled figure from the reconciliation summary. */
function Figure({ value, label }: { value: number | null; label: string }) {
  return (
    <p className="text-sm">
      <b className="tabular-nums">{formatNumber(value)}</b>{' '}
      <span className="text-muted-foreground">{label}</span>
    </p>
  );
}

export function ReconcileDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();

  const archive = useArchiveBacklog();
  const reconcile = useReconcile();

  const archivePreview = archive.data ?? null;
  const preview = reconcile.data ?? null;
  const misses = preview?.misses;

  const failed = () => toast.error(t('paymentReview.actionFailed'));

  function runArchive(apply: boolean) {
    archive.mutate(apply, {
      onSuccess: (result) => {
        if (apply) {
          toast.success(
            t('paymentReview.archivedBookings', { count: result?.matched ?? 0 })
          );
        }
      },
      onError: failed,
    });
  }

  function runReconcile(apply: boolean) {
    reconcile.mutate(apply, { onError: failed });
  }

  const nothingToArchive = !archivePreview || (archivePreview.matched ?? 0) === 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-3xl"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle>{t('paymentReview.findOldPayments')}</SheetTitle>
        </SheetHeader>

        <div className="space-y-5 p-4">
          {/* ------------------------------------------- close out the old */}
          <section className="space-y-2 rounded-xl border p-3">
            <h3 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
              {t('paymentReview.olderBookings')}
            </h3>
            <p className="text-sm">
              {t('paymentReview.olderBookingsHelp', {
                date: formatDate(yearStart()),
              })}
            </p>

            {archivePreview && (
              <Note tone={archivePreview.applied ? 'success' : 'warning'}>
                {archivePreview.applied
                  ? t('paymentReview.archivedBookings', {
                      count: archivePreview.matched ?? 0,
                    })
                  : t('paymentReview.closeOutPreview', {
                      count: archivePreview.matched ?? 0,
                      amount: formatCurrency(archivePreview.amount),
                    })}
              </Note>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={archive.isPending}
                onClick={() => runArchive(false)}
              >
                {archive.isPending && <Loader2 className="animate-spin" />}
                {t('paymentReview.preview')}
              </Button>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive"
                    disabled={nothingToArchive || archive.isPending}
                  >
                    {t('paymentReview.closeThemOut')}
                  </Button>
                </AlertDialogTrigger>
                <ConfirmContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {t('paymentReview.closeThemOut')}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('paymentReview.closeOutApplyPrompt', {
                        count: archivePreview?.matched ?? 0,
                      })}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      onClick={() => runArchive(true)}
                    >
                      {t('common.yes')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </ConfirmContent>
              </AlertDialog>
            </div>
          </section>

          {/* -------------------------------------------- search this year */}
          <Note tone="info">{t('paymentReview.findOldPaymentsHelp')}</Note>

          {preview && (
            <div className="space-y-4">
              <div className="grid gap-x-4 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
                <Figure
                  value={preview.examined}
                  label={t('paymentReview.bookingsExamined')}
                />
                <Figure
                  value={preview.transactionsScanned}
                  label={t('paymentReview.paypalPaymentsScanned')}
                />
                <Figure
                  value={preview.alreadyRecorded}
                  label={t('paymentReview.alreadyRecordedElsewhere')}
                />
                <Figure
                  value={preview.benefitsRelinked}
                  label={t('paymentReview.benefitsRelinked')}
                />
                <Figure
                  value={preview.matchedByCustomId}
                  label={t('paymentReview.matchedByReference')}
                />
                <Figure
                  value={preview.matchedByEvidence}
                  label={t('paymentReview.matchedByEvidence')}
                />
                <Figure
                  value={preview.ambiguous}
                  label={t('paymentReview.needsAChoice')}
                />
              </div>

              {(preview.unresolved ?? 0) > 0 && misses && (
                <div className="space-y-2 rounded-xl border p-3">
                  <h3 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                    {t('paymentReview.whyNoMatch', {
                      count: preview.unresolved ?? 0,
                    })}
                  </h3>
                  <div className="space-y-1.5">
                    <Figure
                      value={misses.noPaymentFromPayer}
                      label={t('paymentReview.missNoPaymentFromPayer')}
                    />
                    <Figure
                      value={misses.payerFoundWrongAmount}
                      label={t('paymentReview.missWrongAmount')}
                    />
                    <Figure
                      value={misses.rightAmountAndTimeWrongPayer}
                      label={t('paymentReview.missWrongPayer')}
                    />
                    <p className="text-sm">
                      <b className="tabular-nums">
                        {formatNumber(misses.payerAndAmountOutsideWindow)}
                      </b>{' '}
                      <span className="text-muted-foreground">
                        {t('paymentReview.missOutsideWindow', {
                          hours: preview.matchWindowHours ?? 0,
                        })}
                        {misses.closestOutsideWindowHours != null &&
                          ` ${t('paymentReview.missClosestGap', {
                            hours: misses.closestOutsideWindowHours,
                          })}`}
                      </span>
                    </p>
                  </div>
                </div>
              )}

              {!preview.autoApplyEnabled && (
                <Note tone="warning">{t('paymentReview.previewOnly')}</Note>
              )}

              {(preview.warnings ?? []).map((warning) => (
                <Note key={warning} tone="error">
                  {warning}
                </Note>
              ))}

              {(preview.rows ?? []).length > 0 && (
                <div className="divide-y rounded-xl border">
                  {preview.rows!.map((row) => (
                    <div
                      key={`${row.bookingId}-${row.transactionId ?? 'none'}`}
                      className="flex items-start justify-between gap-3 p-3"
                    >
                      <div className="min-w-0 space-y-1">
                        <p className="truncate text-sm font-medium">
                          {row.userName}
                        </p>
                        <p className="text-muted-foreground truncate text-xs">
                          {row.userEmail}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {row.eventName} ·{' '}
                          {formatDateTime(row.timeslotStartTime)}
                        </p>
                        <Badge
                          variant={
                            row.applied
                              ? 'success'
                              : row.outcome === 'none'
                                ? 'destructive'
                                : 'default'
                          }
                        >
                          {t(outcomeLabelKey(row.outcome))}
                        </Badge>
                        {row.note && (
                          <p className="text-muted-foreground text-xs">
                            {row.note}
                          </p>
                        )}
                      </div>
                      <p className="shrink-0 text-sm font-semibold tabular-nums">
                        {formatCurrency(row.amount ?? row.price)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('paymentReview.close')}
            </Button>
            <Button
              variant="outline"
              disabled={reconcile.isPending}
              onClick={() => runReconcile(false)}
            >
              {reconcile.isPending && <Loader2 className="animate-spin" />}
              {t('paymentReview.preview')}
            </Button>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button disabled={!preview || reconcile.isPending}>
                  {t('paymentReview.applyChanges')}
                </Button>
              </AlertDialogTrigger>
              <ConfirmContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t('paymentReview.applyChanges')}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('paymentReview.reconcileApplyPrompt')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction onClick={() => runReconcile(true)}>
                    {t('common.yes')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </ConfirmContent>
            </AlertDialog>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
